import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'qampo-ui-library';
import type { HeatmapCell as HeatmapCellData, Skill, StaffMember, Weekday } from '../../../types/weekOverview';
import { WEEKDAYS_ORDER, WEEKDAY_FULL_TRANSLATION_KEYS } from '../../../constants/weekOverviewConstants';
import type { HeatmapNavColumn, HeatmapNavKey } from '../../../services/heatmapNavigationService';
import {
  HEATMAP_ROW_HEADER_COLUMN,
  HEATMAP_TOTAL_COLUMN,
  buildHeatmapCellId,
  skillRowId,
  staffRowId
} from '../../../services/heatmapNavigationService';
import { OpenCalendarButton } from '../../UI';
import type { HeatmapCellNavProps } from './HeatmapCell';
import { HeatmapCell } from './HeatmapCell';
import { HeatmapNavButton } from './HeatmapNavButton';
import styles from './WeekHeatmap.module.css';

interface HeatmapRowProps {
  skill: Skill;
  cells: HeatmapCellData[];
  staffMembers: StaffMember[];
  staffCells: HeatmapCellData[];
  isExpanded: boolean;
  tabStopCellId: string | null;
  onToggle: (skillId: string) => void;
  onCellClick: (skillId: string, staffId: string | null, day: Weekday) => void;
  onCellHover: (cell: HeatmapCellData | null, position: { x: number; y: number } | null) => void;
  onStaffSelect: (staffId: string) => void;
  onCellNavigate: (cellId: string, key: HeatmapNavKey) => void;
  onCellFocus: (cellId: string) => void;
}

// Aggregates a row's daily cells into a single week-total cell. Utilization is
// the mean of the daily percentages (the v4 behaviour); hours are summed.
function computeRowTotal(cells: HeatmapCellData[]): HeatmapCellData | null {
  const valid = cells.filter((cell) => !cell.isClosed);
  if (valid.length === 0) return null;
  const booked = valid.reduce((acc, cell) => acc + cell.bookedHours, 0);
  const available = valid.reduce((acc, cell) => acc + cell.availableHours, 0);
  const staffScheduled = Math.round(valid.reduce((acc, cell) => acc + cell.staffScheduled, 0) / valid.length);
  const staffOnLeave = Math.round(valid.reduce((acc, cell) => acc + cell.staffOnLeave, 0) / valid.length);
  const utilizationPercent = Math.round(
    valid.reduce((acc, cell) => acc + cell.utilizationPercent, 0) / valid.length
  );
  return {
    skillId: valid[0].skillId,
    staffId: valid[0].staffId,
    day: 'MON',
    bookedHours: Number(booked.toFixed(1)),
    availableHours: Number(available.toFixed(1)),
    utilizationPercent,
    staffScheduled,
    staffOnLeave,
    isClosed: false
  };
}

export const HeatmapRow = ({
  skill,
  cells,
  staffMembers,
  staffCells,
  isExpanded,
  tabStopCellId,
  onToggle,
  onCellClick,
  onCellHover,
  onStaffSelect,
  onCellNavigate,
  onCellFocus
}: HeatmapRowProps) => {
  const { t } = useTranslation();

  const cellsByDay = useMemo(() => {
    const map = new Map<Weekday, HeatmapCellData>();
    for (const cell of cells) map.set(cell.day, cell);
    return map;
  }, [cells]);

  const totalCell = useMemo(() => computeRowTotal(cells), [cells]);

  // Interactive cells all read "42%", so the row they belong to has to come from
  // an accessible name rather than the surrounding grid. A closed day has no
  // utilization to report — its percentage is a placeholder zero — so it is left
  // to be named by its own "—" and described by its tooltip.
  const buildCellLabel = (name: string, cell: HeatmapCellData): string | undefined =>
    cell.isClosed
      ? undefined
      : t('week-overview-heatmap-cell-label', {
          name,
          day: t(WEEKDAY_FULL_TRANSLATION_KEYS[cell.day]),
          percent: cell.utilizationPercent
        });

  // Every cell of a row joins the arrow navigation, so each one needs its id and
  // whether it currently holds the grid's single tab stop.
  const cellNavProps = (rowId: string, column: HeatmapNavColumn): HeatmapCellNavProps => {
    const cellId = buildHeatmapCellId(rowId, column);
    return {
      cellId,
      isTabStop: cellId === tabStopCellId,
      onNavigate: onCellNavigate,
      onCellFocus
    };
  };

  return (
    <>
      <HeatmapNavButton
        className={styles.rowLabelCell}
        isExpanded={isExpanded}
        onActivate={() => onToggle(skill.id)}
        {...cellNavProps(skillRowId(skill.id), HEATMAP_ROW_HEADER_COLUMN)}
      >
        <ChevronDown className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`} />
        {skill.name}
      </HeatmapNavButton>
      {WEEKDAYS_ORDER.map((day) => {
        const cell = cellsByDay.get(day);
        if (!cell) return <div key={day} className={styles.cell} />;
        return (
          <HeatmapCell
            key={day}
            cell={cell}
            label={buildCellLabel(skill.name, cell)}
            onClick={() => onCellClick(skill.id, null, day)}
            onHover={onCellHover}
            {...cellNavProps(skillRowId(skill.id), day)}
          />
        );
      })}
      {totalCell ? (
        <HeatmapCell
          cell={totalCell}
          onHover={onCellHover}
          {...cellNavProps(skillRowId(skill.id), HEATMAP_TOTAL_COLUMN)}
        />
      ) : (
        <div className={styles.cell} />
      )}

      {isExpanded &&
        staffMembers.map((staff) => {
          const staffCellsForMember = staffCells.filter((cell) => cell.staffId === staff.id);
          const byDay = new Map<Weekday, HeatmapCellData>();
          for (const cell of staffCellsForMember) byDay.set(cell.day, cell);
          const staffTotal = computeRowTotal(staffCellsForMember);
          const staffLabel = t('week-overview-open-calendar-for', { name: staff.name });
          return (
            <div key={staff.id} style={{ display: 'contents' }}>
              <div className={`${styles.rowLabelCell} ${styles.rowLabelCellChild}`}>
                {/* The person's name is the row header, and opening their
                    calendar is what this row activates — the icon button beside
                    it is the same action with a smaller hit area. */}
                <HeatmapNavButton
                  className={styles.staffName}
                  label={staffLabel}
                  onActivate={() => onStaffSelect(staff.id)}
                  {...cellNavProps(staffRowId(skill.id, staff.id), HEATMAP_ROW_HEADER_COLUMN)}
                >
                  {staff.name}
                </HeatmapNavButton>
                <span className={styles.openCalendarSlot}>
                  <OpenCalendarButton label={staffLabel} onClick={() => onStaffSelect(staff.id)} />
                </span>
              </div>
              {WEEKDAYS_ORDER.map((day) => {
                const staffCell = byDay.get(day);
                if (!staffCell) return <div key={day} className={`${styles.cell} ${styles.cellChild}`} />;
                return (
                  <HeatmapCell
                    key={day}
                    cell={staffCell}
                    label={buildCellLabel(staff.name, staffCell)}
                    isChild
                    onClick={() => onCellClick(skill.id, staff.id, day)}
                    onHover={onCellHover}
                    {...cellNavProps(staffRowId(skill.id, staff.id), day)}
                  />
                );
              })}
              {staffTotal ? (
                <HeatmapCell
                  cell={staffTotal}
                  isChild
                  onHover={onCellHover}
                  {...cellNavProps(staffRowId(skill.id, staff.id), HEATMAP_TOTAL_COLUMN)}
                />
              ) : (
                <div className={`${styles.cell} ${styles.cellChild}`} />
              )}
            </div>
          );
        })}
    </>
  );
};
